export type DaRole =
  | "driver"
  | "absent"
  | "rescue"
  | "standby"
  | "training"
  | "quality"
  | "special"
  | "clientCancelled"
  | "flex"
  | "dispatcher"
  | "supervisor"
  | "fleetCoordinator"
  | "leadDriver"
  | "helper"
  | "supportDispatcher"
  | "mycuCancelled"
  | "rejected"
  | "performanceCoordinator";

export const daRoles: Record<
  DaRole,
  {
    roleScheduleId: number;
    roleId: number | null;
    name: string;
    shortName: string;
    baseHours: number;
    flexible: boolean;
  }
> = {
  driver: {
    roleScheduleId: 0,
    roleId: 0,
    name: "Standard",
    shortName: "Stan",
    baseHours: 10,
    flexible: false,
  },
  absent: {
    roleScheduleId: 1,
    roleId: 1,
    name: "No Show",
    shortName: "NS",
    baseHours: 0,
    flexible: false,
  },
  rescue: {
    roleScheduleId: 2,
    roleId: 2,
    name: "Rescue",
    shortName: "Resc",
    baseHours: 6,
    flexible: true,
  },
  standby: {
    roleScheduleId: 3,
    roleId: 3,
    name: "Standby",
    shortName: "Stdby",
    baseHours: 3,
    flexible: false,
  },
  training: {
    roleScheduleId: 4,
    roleId: 4,
    name: "Training",
    shortName: "Train",
    baseHours: 3,
    flexible: false,
  },
  quality: {
    roleScheduleId: 5,
    roleId: 5,
    name: "Quality",
    shortName: "Qual",
    baseHours: 6,
    flexible: true,
  },
  special: {
    roleScheduleId: 6,
    roleId: 6,
    name: "Special",
    shortName: "Spec",
    baseHours: 0,
    flexible: true,
  },
  clientCancelled: {
    roleScheduleId: 7,
    roleId: 7,
    name: "Client Cancellation",
    shortName: "Client Cancl",
    baseHours: 0,
    flexible: false,
  },
  flex: {
    roleScheduleId: 8,
    roleId: 8,
    name: "Flex",
    shortName: "Flex",
    baseHours: 1,
    flexible: true,
  },
  dispatcher: {
    roleScheduleId: 9,
    roleId: null,
    name: "Dispatcher",
    shortName: "Dispt",
    baseHours: 12,
    flexible: true,
  },
  supervisor: {
    roleScheduleId: 10,
    roleId: null,
    name: "Supervisor",
    shortName: "Superv",
    baseHours: 8,
    flexible: true,
  },
  fleetCoordinator: {
    roleScheduleId: 11,
    roleId: null,
    name: "Fleet Coordinator",
    shortName: "FltCoord",
    baseHours: 8,
    flexible: true,
  },
  leadDriver: {
    roleScheduleId: 12,
    roleId: null,
    name: "Lead Driver",
    shortName: "LdDrv",
    baseHours: 6,
    flexible: true,
  },
  helper: {
    roleScheduleId: 13,
    roleId: null,
    name: "Helper",
    shortName: "Helper",
    baseHours: 0,
    flexible: true,
  },
  supportDispatcher: {
    roleScheduleId: 14,
    roleId: null,
    name: "Support Dispatcher",
    shortName: "SDispat",
    baseHours: 6,
    flexible: true,
  },
  mycuCancelled: {
    roleScheduleId: 15,
    roleId: 9,
    name: "MYCU Cancellation",
    shortName: "MYCU Cancl",
    baseHours: 0,
    flexible: false,
  },
  rejected: {
    roleScheduleId: 16,
    roleId: 10,
    name: "Rejected",
    shortName: "Rejct",
    baseHours: 0,
    flexible: false,
  },
  performanceCoordinator: {
    roleScheduleId: 17,
    roleId: 11,
    name: "Performance Coordinator",
    shortName: "PerfCoord",
    baseHours: 8,
    flexible: true,
  },
};

// This function returns a object like { 0: "driver" }.
export const daScheduleRoleIdToKey: Record<number, DaRole> = Object.fromEntries(
  (Object.keys(daRoles) as DaRole[]).map((key) => [daRoles[key].roleScheduleId, key]),
);

// This function returns a object like { 0: "Standard" }.
export const daRoleIdToKey: Record<number, DaRole> = Object.fromEntries(
  (Object.keys(daRoles) as DaRole[]).map((key) => [daRoles[key].roleId, daRoles[key].name]),
);

// This function returns a object like { "driver": 0 }.
export const daScheduleRoleKeyToId: Record<DaRole, number> = Object.fromEntries(
  (Object.keys(daRoles) as DaRole[]).map((key) => [key, daRoles[key].roleScheduleId,]),
) as Record<DaRole, number>;

// This function returns a object like "Standart".
export const daScheduleRoleName: Record<DaRole, string> = Object.fromEntries(
  (Object.keys(daRoles) as DaRole[]).map((key) => ["name", daRoles[key].name]),
) as Record<DaRole, string>;

export const roleDa = [
  daRoles.driver,
  daRoles.absent,
  daRoles.rescue,
  daRoles.standby,
  daRoles.training,
  daRoles.quality,
  daRoles.special,
  daRoles.clientCancelled,
  daRoles.mycuCancelled,
  daRoles.flex,
  daRoles.rejected,
  daRoles.performanceCoordinator,
]
  .map((role) => ({ ...role, value: role.roleId }))
  .filter((el) => el.value !== null);

export const scheduleRoleDa = [
  daRoles.driver,
  daRoles.absent,
  daRoles.rescue,
  daRoles.standby,
  daRoles.training,
  daRoles.quality,
  daRoles.special,
  daRoles.clientCancelled,
  daRoles.mycuCancelled,
  daRoles.flex,
  daRoles.helper,
  daRoles.dispatcher,
  daRoles.supportDispatcher,
  daRoles.supervisor,
  daRoles.fleetCoordinator,
  daRoles.leadDriver,
  daRoles.rejected,
  daRoles.performanceCoordinator,
].map((role) => ({ ...role, value: role.roleScheduleId }));

export const roleFleet = [
  { id: 1, name: "Standard", value: 0 },
  { id: 2, name: "Standby", value: 1 },
  { id: 3, name: "Out of Service", value: 2 },
];

export const getRoleDaById = (number: number) => {
  return roleDa.find((el) => el.value === number)?.name;
};

export const getRoleFleetById = (number: number) => {
  return roleFleet.find((el) => el.value === number)?.name;
};

export const waveDa = [
  { id: 1, name: "Wave 1", value: 1 },
  { id: 2, name: "Wave 2", value: 2 },
  { id: 3, name: "Wave 3", value: 3 },
  { id: 4, name: "Wave 4", value: 4 },
  { id: 5, name: "Wave 5", value: 5 },
  { id: 6, name: "Wave 6", value: 6 },
  { id: 7, name: "Wave 7", value: 7 },
  { id: 8, name: "Wave 8", value: 8 },
  { id: 9, name: "Wave 9", value: 9 },
  { id: 10, name: "Wave 10", value: 10 },
];
